import { Product } from "@omni/types/api/product/bff/product.model";
import { ProductFieldSet } from "@omni/types/api/product/bff/shared/enums/productFieldSet.enum";
import { ProductTileFeature } from "@omni/types/api/product/bff/shared/productTileFeature.model";
import { Media } from "@omni/types/api/shared/bff/media.model";
import { isDefined } from "@omni/utils/helper.utils";

export type Sections = Record<
    string,
    Record<
        string,
        {
            toolTipImage?: Media | null;
        }
    >
>;

const mergeFeatureInSections = (
    sections: Sections,
    feature: ProductTileFeature
) => ({
    ...sections,
    [feature.kind!.name]: {
        ...(sections[feature.kind!.name] || {}),
        [feature.name]: {
            toolTipImage: feature.toolTipImage
        }
    }
});

export const getSections = (
    products: Array<Product<ProductFieldSet.ProductTile>>
) =>
    products
        .flatMap(({ features }) => features)
        .reduce<Sections>(
            (sections, feature) => mergeFeatureInSections(sections, feature),
            {}
        );

export const getDistinctSections = (
    products: Array<Product<ProductFieldSet.ProductTile>>
) => {
    const featureValuesMap = products.reduce<
        Map<string, Array<{ featureValue: string; productCount: number }>>
    >((acc, product) => {
        product.features.forEach(({ name, featureValues }) => {
            const featureV = acc.get(name);
            const formattedFeatureValue = featureValues
                .map((fv) => fv.formattedValue)
                .join(", ");
            if (isDefined(featureV)) {
                const featureByValue = featureV.find(
                    (fv) => fv.featureValue === formattedFeatureValue
                );
                if (featureByValue) {
                    featureByValue.productCount += 1;
                } else {
                    acc.set(name, [
                        ...featureV,
                        {
                            productCount: 1,
                            featureValue: formattedFeatureValue
                        }
                    ]);
                }
            } else {
                acc.set(name, [
                    {
                        featureValue: formattedFeatureValue,
                        productCount: 1
                    }
                ]);
            }
        });

        return acc;
    }, new Map());

    return products
        .flatMap(({ features }) => features)
        .reduce<Sections>((sections, feature) => {
            const uniqueFeatureValuesCount = featureValuesMap.get(feature.name);

            if (
                uniqueFeatureValuesCount?.some(
                    (fvc) => fvc.productCount === 1
                ) ||
                (uniqueFeatureValuesCount?.length === 1 &&
                    uniqueFeatureValuesCount?.at(0)?.productCount !==
                        products.length)
            ) {
                return mergeFeatureInSections(sections, feature);
            }

            return sections;
        }, {});
};
